import { FormHelperText, Stack } from '@mui/material';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { Title } from './Title';
import { Form } from './Form';
import { useExpenseClaimForm } from 'Hooks/useExpenseClaimForm';
import { FormProvider } from 'react-hook-form';
import { AddClaimButton } from './AddClaimButton';
import { useContext, useRef, useState } from 'react';
import {
  ExpenseClaimContext,
  claimInitialValues,
} from 'Context/ExpenseClaimContext';
import { ListItem } from './ListItem';
import { CheckboxInput, DeleteModal } from 'Components';
import { IExpenseClaim, ITravelExpensesRefund } from 'Interfaces/expenseClaim';
import { scrollToStartOfDiv } from 'Utils';
import { RequestsContext } from 'Context';
import { scrollToBottom } from 'Utils/scrollToBottom';
import { FormActionButtons } from 'Modules/Requests/FormActionButtons';
import { useNavigate } from 'react-router-dom';

interface IExpenseClaimMain {
  nextStep: () => void;
}

interface IModalData {
  title?: string;
  subtitle?: string;
  approveButtonText?: string;
  declineButtonText?: string;
  fileName: string;
  deleteFile: () => void;
}

const editModalInitialData: Partial<IModalData> =
  Dictionary[DICTIONARY.ExpenseClaim].editModalInitialData;

export const ExpenseClaimMain = ({ nextStep }: IExpenseClaimMain) => {
  const { setRequestsData } = useContext(RequestsContext);

  const { claims, addClaim, deleteClaim } = useContext(ExpenseClaimContext);
  const [isFormOpen, setIsFormOpen] = useState(true);
  const [checkbox, setCheckbox] = useState(false);
  const [checkboxErrorMessage, setCheckboxErrorMessage] = useState<
    string | null
  >(null);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState<IModalData | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const dictionary = useDictionary(Dictionary, DICTIONARY.ExpenseClaim);
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();

  const expenseClaimForm = useExpenseClaimForm();

  const onSubmit = async (
    event:
      | React.FormEvent<HTMLFormElement>
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.preventDefault();

    await expenseClaimForm.trigger();

    if (!isFormOpen && (!claims || claims.length === 0)) {
      setErrorMessage(dictionary.mustFillOneForm);
    } else setErrorMessage(null);

    if (!checkbox) {
      setCheckboxErrorMessage(dictionary.mustCheckCheckbox);
      scrollToBottom();
      return;
    } else setCheckboxErrorMessage(null);

    if (
      (!isFormOpen && claims?.length > 0) ||
      (isFormOpen &&
        Object.keys(expenseClaimForm.formState.errors).length === 0)
    ) {
      setRequestsData((prev) => {
        const currentClaims = [...claims];
        if (isFormOpen) currentClaims.push({ ...expenseClaimForm.getValues() });

        const travelExpensesRefund: ITravelExpensesRefund = {
          travelExpenses: currentClaims,
          isApproved: true,
        };

        return {
          ...prev,
          requestDataJson: travelExpensesRefund,
        };
      });

      nextStep();
    }
  };

  const onAddClaim = async () => {
    if (!isFormOpen) setIsFormOpen(true);
    else
      await expenseClaimForm.handleSubmit((data) => {
        addClaim({ ...data });
        expenseClaimForm.reset({ ...claimInitialValues });
      })();
  };

  const onDuplicateClaim = async (claim?: IExpenseClaim) => {
    if (claim) addClaim({ ...claim });
    else {
      await expenseClaimForm.handleSubmit((data) => {
        addClaim({ ...data });
      })();
    }
  };

  const onFormClose = () => setIsFormOpen(false);

  const onEditClaim = async (claimIndex: number) => {
    const claim = claims[claimIndex];
    deleteClaim(claimIndex);
    await onAddClaim();
    expenseClaimForm.reset(claim);
    setTimeout(
      () => scrollToStartOfDiv(formWrapperRef, isDesktop ? 130 : 100),
      0
    );
  };

  const formWrapperRef = useRef<HTMLDivElement>(null);

  const onDeleteConfirmation = (claimIndex: number) => {
    const claim = claims[claimIndex];
    setModalData({
      fileName: dictionary.getFileName(
        claim.originPlace,
        claim.destinationPlace
      ),
      deleteFile: () => {
        deleteClaim(claimIndex);
        setShowModal(false);
      },
    });
    setShowModal(true);
  };

  const onEditConfirmation = (claimIndex: number) => {
    const claim = claims[claimIndex];
    setModalData({
      ...editModalInitialData,
      fileName: dictionary.getFileName(
        claim.originPlace,
        claim.destinationPlace
      ),
      deleteFile: () => {
        onEditClaim(claimIndex);
        setShowModal(false);
      },
    });
    setShowModal(true);
  };

  return (
    <Stack gap={isDesktop ? '40px' : '24px'} mt={'40px'}>
      <Title />
      {claims.map((claim, index) => (
        <ListItem
          key={index}
          claim={claim}
          onEditClaim={() => onEditConfirmation(index)}
          onDeleteClaim={() => onDeleteConfirmation(index)}
          onDuplicateClaim={() => onDuplicateClaim(claim)}
        />
      ))}
      <DeleteModal
        isVisible={showModal}
        closeModal={() => setShowModal(false)}
        deleteFile={modalData?.deleteFile || (() => {})}
        fileName={modalData?.fileName || ''}
        title={modalData?.title || ''}
        subtitle={modalData?.subtitle || ''}
        approveButtonText={modalData?.approveButtonText || ''}
        declineButtonText={modalData?.declineButtonText || ''}
      />
      <form
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          marginBottom: '200px',
          alignItems: 'center',
        }}
        onSubmit={onSubmit}
        noValidate
      >
        <FormProvider {...expenseClaimForm}>
          {isFormOpen && (
            <Form
              formWrapperRef={formWrapperRef}
              onDuplicate={() => onDuplicateClaim()}
              onFormClose={onFormClose}
            />
          )}
          <AddClaimButton onClick={onAddClaim} />
        </FormProvider>
        <Stack maxWidth={isDesktop ? '770px' : undefined}>
          <Stack
            mt={'16px'}
            alignItems={'center'}
            justifyContent={'flex-start'}
            flexDirection={'row'}
            gap={'12px'}
            sx={{
              '.MuiSvgIcon-root': {
                fill:
                  checkboxErrorMessage !== null && !checkbox
                    ? '#d32f2f'
                    : undefined,
              },
            }}
          >
            <CheckboxInput
              checked={checkbox}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setCheckbox(e.target.checked)
              }
              label={dictionary.declaration}
              textVariant={'body3'}
              textSx={{
                color:
                  !checkbox && checkboxErrorMessage
                    ? '#E12D2D'
                    : 'text.primary',
              }}
              containerStyle={{ justifyContent: 'center' }}
            />
          </Stack>
          {!checkbox && (
            <FormHelperText
              sx={{ textAlign: 'center', fontSize: '14px' }}
              role={'alert'}
              error
            >
              {checkboxErrorMessage}
            </FormHelperText>
          )}
        </Stack>
        <FormActionButtons
          next={onSubmit}
          previous={() => navigate('/universalRequest')}
        />
        <FormHelperText
          sx={{ textAlign: 'center', fontSize: '14px' }}
          role={'alert'}
          error
        >
          {errorMessage}
        </FormHelperText>
      </form>
    </Stack>
  );
};
